import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmProductSeriesFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { sectionMessages, statusMessages } from "../../messages";
import { wsmSeriesAddPath, wsmSeriesUrl } from "../../urls";
import { getAxisLabels } from "../../utils";
import { columnMessages, messages } from "./messages";

const numberOfColumns = 6;

export interface WsmSeriesListPageProps {
  series: WsmProductSeriesFragment[] | undefined;
  /** The store's own product attributes: what turns an axis slug into a name. */
  attributes: Array<{ slug?: string | null; name?: string | null }>;
  query: string;
  rowNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onRowNumberChange: (rowNumber: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmSeriesListPage = ({
  series,
  attributes,
  query,
  rowNumber,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onRowNumberChange,
  onNextPage,
  onPreviousPage,
}: WsmSeriesListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.sectionTitle)}>
        <Box display="flex" alignItems="center" gap={3}>
          <Button onClick={() => navigate(wsmSeriesAddPath)} data-test-id="wsm-series-list-create">
            {intl.formatMessage(messages.createSeries)}
          </Button>
        </Box>
      </TopNav>
      <DashboardCard data-test-id="wsm-series-list">
        <DashboardCard.Content paddingX={0}>
          <Box paddingX={6} paddingTop={4} paddingBottom={2}>
            <SearchInput
              initialSearch={query}
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              onSearchChange={onQueryChange}
            />
          </Box>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(columnMessages.name)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.brand)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.axes)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.partitioningAxis)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.members)}</TableCell>
                <TableCell>{intl.formatMessage(columnMessages.status)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                series,
                row => (
                  <TableRowLink
                    key={row ? row.id : "skeleton"}
                    hover={!!row}
                    href={row ? wsmSeriesUrl(row.id) : undefined}
                    data-test-id="wsm-series-row"
                  >
                    <TableCell data-test-id="wsm-series-row-name">
                      {row ? (
                        <>
                          <Text size={3} display="block">
                            {row.name}
                          </Text>
                          {row.tagline && (
                            <Text size={2} color="default2" display="block">
                              {row.tagline}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>{row ? row.brand || "-" : <Skeleton />}</TableCell>
                    <TableCell data-test-id="wsm-series-row-axes">
                      {row ? getAxisLabels(row.axes, attributes).join(", ") || "-" : <Skeleton />}
                    </TableCell>
                    <TableCell data-test-id="wsm-series-row-partitioning-axis">
                      {row ? (
                        getAxisLabels(
                          row.partitioningAxis ? [row.partitioningAxis] : [],
                          attributes,
                        )[0] || "-"
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>{row ? row.memberCount : <Skeleton />}</TableCell>
                    <TableCell>
                      {row ? (
                        <Pill
                          data-test-id="wsm-series-row-status"
                          color={row.published ? "success" : "error"}
                          label={intl.formatMessage(
                            row.published ? statusMessages.live : statusMessages.draft,
                          )}
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-series-list-empty">
                    <TableCell colSpan={numberOfColumns}>
                      <Text size={3} color="default2">
                        {intl.formatMessage(messages.noSeries)}
                      </Text>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={{ rowNumber }}
                  onUpdateListSettings={(_, value) => onRowNumberChange(value as number)}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={onNextPage}
                  onPreviousPage={onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmSeriesListPage;
